import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageSquareQuote, Loader2, X } from "lucide-react";
import { Button } from "../ui/button";
import { adminPendingReview, moderateReview } from "../../lib/api";

const SNOOZE_KEY = "hv_review_nudge_snoozed_until";

// Owner-only strip on the Vault home: the oldest pending testimonial with Approve / Hide, so it never waits for Monday's digest.
export const PendingReviewNudge = () => {
  const qc = useQueryClient();
  const [snoozed, setSnoozed] = useState(() => Number(localStorage.getItem(SNOOZE_KEY) || 0) > Date.now());
  const { data } = useQuery({ queryKey: ["pending-review-nudge"], queryFn: adminPendingReview, staleTime: 5 * 60_000, retry: false, enabled: !snoozed });
  const [busy, setBusy] = useState("");
  const r = data?.review;
  if (snoozed || !r) return null;
  const act = async (status) => {
    setBusy(status);
    try {
      await moderateReview(r.id, status);
      toast.success(status === "approved" ? "Approved — it's live on the testimonials strip" : "Hidden");
      qc.invalidateQueries({ queryKey: ["pending-review-nudge"] }); qc.invalidateQueries({ queryKey: ["admin-attention"] }); qc.invalidateQueries({ queryKey: ["reviews"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update the review"); } finally { setBusy(""); }
  };
  const snooze = () => { localStorage.setItem(SNOOZE_KEY, String(Date.now() + 24 * 3600 * 1000)); setSnoozed(true); };
  return (
    <section className="rounded-2xl border border-primary/30 bg-primary/5 p-4 sm:p-5 flex gap-3 items-start" data-testid="pending-review-nudge" data-review-id={r.id}>
      <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><MessageSquareQuote className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-primary">A family left a review{data.count > 1 ? ` · ${data.count} waiting` : ""}</div>
        <p className="mt-1 font-serif text-base leading-snug" data-testid="pending-review-text">“{r.text}”</p>
        <div className="mt-1 text-xs text-muted-foreground">{r.display_name || "A family"}{r.rating ? ` · ${"★".repeat(r.rating)}` : ""}</div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" className="rounded-full h-8" disabled={!!busy} onClick={() => act("approved")} data-testid="pending-review-approve">{busy === "approved" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Approve"}</Button>
          <Button size="sm" variant="outline" className="rounded-full h-8" disabled={!!busy} onClick={() => act("hidden")} data-testid="pending-review-hide">{busy === "hidden" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Hide"}</Button>
          <Link to="/admin#reviews-moderation" className="text-xs text-primary hover:underline" data-testid="pending-review-all">{data.count > 1 ? "See all" : "Open reviews"}</Link>
        </div>
      </div>
      <button type="button" onClick={snooze} aria-label="Remind me tomorrow" title="Remind me tomorrow" className="h-7 w-7 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-background transition-colors shrink-0" data-testid="pending-review-snooze"><X className="h-3.5 w-3.5" /></button>
    </section>
  );
};
